import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { movieGenres, movieList } from "@/mocks/movies";
import CategoryHero from "@/components/feature/CategoryHero";
import MovieCard from "@/components/feature/MovieCard";
import EmptyState from "@/components/feature/EmptyState";
import MovieFilters, { type MovieFilterValue } from "./components/MovieFilters";
import useDocumentMeta from "@/hooks/useDocumentMeta";

const PAGE_SIZE = 8;

const ratingThreshold: Record<string, number> = { "9": 9, "8.5": 8.5, "8": 8 };

export default function MoviesList() {
  useDocumentMeta(
    "影视推荐 - 色花堂",
    "色花堂影视推荐频道，精选剧集与电影，支持按类型、地区、年份与评分多维筛选，帮你快速发现值得一看的好作品。",
  );
  const [searchParams] = useSearchParams();
  const genreParam = searchParams.get("genre");
  const initialGenre = genreParam && movieGenres.includes(genreParam) ? genreParam : "全部";

  const [filter, setFilter] = useState<MovieFilterValue>({
    genre: initialGenre,
    region: "全部",
    year: "全部",
    rating: "all",
    sort: "latest",
  });
  const [visible, setVisible] = useState(PAGE_SIZE);

  const handleChange = (value: MovieFilterValue) => {
    setFilter(value);
    setVisible(PAGE_SIZE);
  };

  const filtered = useMemo(() => {
    const list = movieList.filter((movie) => {
      if (filter.genre !== "全部" && !movie.genre.includes(filter.genre)) return false;
      if (filter.region !== "全部" && movie.region !== filter.region) return false;
      if (filter.year !== "全部" && movie.year !== filter.year) return false;
      if (filter.rating !== "all" && movie.rating < ratingThreshold[filter.rating]) return false;
      return true;
    });
    return list.sort((a, b) => {
      if (filter.sort === "popular") return b.popularity - a.popularity;
      if (filter.sort === "rating") return b.rating - a.rating;
      return b.year - a.year;
    });
  }, [filter]);

  const shown = filtered.slice(0, visible);
  const reset = () => handleChange({ genre: "全部", region: "全部", year: "全部", rating: "all", sort: "latest" });

  return (
    <div>
      <CategoryHero
        title="影视推荐"
        description="精选剧集与电影，按类型、地区、年份与评分多维筛选，快速找到你想看的那一部。"
        icon="ri-movie-2-line"
        crumbs={[{ label: "首页", to: "/" }, { label: "影视推荐" }]}
      />

      <div className="px-4 py-8 md:px-8">
        <div className="mx-auto w-full max-w-[1280px]">
          <MovieFilters value={filter} onChange={handleChange} />

          <p className="mt-4 text-sm text-foreground-500">
            共找到 <span className="font-semibold text-primary-700">{filtered.length}</span> 部影视作品
          </p>

          {shown.length > 0 ? (
            <>
              <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5 lg:grid-cols-4">
                {shown.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
              {visible < filtered.length && (
                <div className="mt-8 flex justify-center">
                  <button
                    type="button"
                    onClick={() => setVisible((prev) => prev + PAGE_SIZE)}
                    className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-6 py-3 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700 cursor-pointer"
                  >
                    <i className="ri-add-line text-base" aria-hidden="true"></i>
                    加载更多（{filtered.length - visible}）
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="mt-4">
              <EmptyState
                icon="ri-movie-line"
                title="没有符合条件的影视作品"
                description="当前筛选组合没有匹配结果，试着放宽类型、地区或评分条件。"
                actionLabel="重置筛选条件"
                onAction={reset}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}